import { useState, useEffect, createContext, useContext, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { getAffiliateCode } from "@/hooks/useAffiliateTracking";
import { getUtmPayload } from "@/hooks/useUtmTracking";

const SITE_URL = import.meta.env.PROD ? "https://www.fcbracket.com" : window.location.origin;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithMagicLink: (email: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // On first sign-in, store affiliate attribution and redirect
        if (event === "SIGNED_IN" && session?.user) {
          // Affiliate attribution (fire-and-forget)
          const affCode = getAffiliateCode();
          if (affCode) {
            supabase.rpc("set_affiliate_referral", { p_affiliate_code: affCode }).then(({ error }) => {
              if (error) console.warn("[affiliate] referral attribution failed:", error.message);
            });
          }

          // UTM attribution — persist first-touch campaign to the profile
          // (idempotent server-side via COALESCE, so this is safe to call on
          // every sign-in; nothing changes after first write).
          const utm = getUtmPayload();
          if (utm) {
            supabase.rpc("set_signup_attribution", {
              p_utm_source:   utm.utm_source   ?? null,
              p_utm_medium:   utm.utm_medium   ?? null,
              p_utm_campaign: utm.utm_campaign ?? null,
              p_utm_content:  utm.utm_content  ?? null,
              p_utm_term:     utm.utm_term     ?? null,
              p_landing_page: utm.landing_page ?? null,
            } as any).then(({ error }) => {
              if (error) console.warn("[utm] attribution failed:", error.message);
            });
          }

          // ⚠ Supabase-js v2 fires SIGNED_IN on every token refresh + session
          // restore from localStorage, not only on explicit sign-in. Without
          // gating, a token refresh while the user is on /groups, /dashboard,
          // etc. would kick them to /bracket via a hard page reload — causing
          // the visible "flicker" / "two pages" behaviour.
          //
          // Only redirect when the user literally just authed (came from
          // /auth or the magic-link landing). Otherwise leave them where
          // they are.
          const path = window.location.pathname;
          const justAuthed = path === "/auth" || path === "/check-email" || path === "/";
          if (!justAuthed) return;

          // Honor a pending `next` path stashed by /auth — sends users back
          // to the protected route they tried to visit before sign-in.
          // Only accept same-origin paths to prevent open-redirect abuse.
          const pendingNext = localStorage.getItem("post_auth_next");
          if (pendingNext && pendingNext.startsWith("/") && !pendingNext.startsWith("//")) {
            localStorage.removeItem("post_auth_next");
            window.location.replace(pendingNext);
            return;
          }

          const { count } = await supabase
            .from("brackets")
            .select("id", { count: "exact", head: true })
            .eq("user_id", session.user.id);
          if (count === 0 && window.location.pathname !== "/bracket") {
            // location.replace — don't push a history entry for the /auth
            // stop so the back button doesn't land them back on it.
            window.location.replace("/bracket");
          }
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, displayName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: SITE_URL,
      },
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signInWithMagicLink = async (email: string) => {
    // If there's a pending invite, redirect back to it after auth
    const pendingInvite = localStorage.getItem("pending_invite_code");
    // Default to `/` (dashboard) so returning users land on their hub.
    // First-time users with zero brackets get redirected to /bracket by
    // the SIGNED_IN handler above.
    const redirectPath = pendingInvite ? `/invite/${pendingInvite}` : "/";
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: SITE_URL + redirectPath,
      },
    });
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${SITE_URL}/reset-password`,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error };
  };

  const signOut = async () => {
    // Force redirect after 2s no matter what — don't let a hanging API call block sign-out
    const forceRedirect = setTimeout(() => {
      localStorage.removeItem("sb-ogxdbwtxadjclizdpugy-auth-token");
      window.location.href = "/";
    }, 2000);

    try {
      const { error } = await supabase.auth.signOut();
      if (error) console.error("Sign out error:", error);
    } catch (e) {
      console.error("Sign out exception:", e);
    }

    clearTimeout(forceRedirect);
    // Clear any remaining session data and hard redirect
    localStorage.removeItem("sb-ogxdbwtxadjclizdpugy-auth-token");
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signInWithMagicLink, resetPassword, updatePassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
